/** @format */
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CompletionMethod, CourseUnit, OpenUniversityCart, OpenUniversityCartItem, OpenUniversityProduct } from 'common-typescript/types';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OpenUniversityEnrichedEnrolmentsService } from '../../../common/service/open-university-enriched-enrolments.service';
import { EnrichedAssItem, EnrichedEnrolment } from '../../../open-university-enrolment-wizard/open-university-enrolment-wizard.component';

/**
 * Study voucher variant of {@link OpenUniversityEnrolledItemInfoComponent}. Mirrors the normal open university path's enrolled item
 * accordion, but renders the product package extent (not the course unit extent) and the study voucher price (0,00 €, paid with a study
 * voucher), matching the study voucher enrolment confirmed view (OTM-35579).
 */
@Component({
    selector: 'app-study-voucher-enrolled-item-info',
    templateUrl: './study-voucher-enrolled-item-info.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StudyVoucherEnrolledItemInfoComponent implements OnInit {
    @Input() cartItem: OpenUniversityCartItem;
    @Input() openUniversityCart: OpenUniversityCart;
    @Input() openUniversityProducts: OpenUniversityProduct[] = [];
    @Input() courseUnits: CourseUnit[] = [];
    @Input() assessmentItemsData: EnrichedAssItem[] = [];

    courseUnit: CourseUnit;
    product: OpenUniversityProduct;
    completionMethod: CompletionMethod;
    openUniStudentEnrolments$: Observable<EnrichedEnrolment[]>;

    constructor(private readonly openUniversityEnrichedEnrolmentsService: OpenUniversityEnrichedEnrolmentsService) {}

    ngOnInit(): void {
        this.product = this.getProductByCartItem();
        this.courseUnit = this.getCourseUnitByProduct();
        this.completionMethod = this.getCompletionMethodByProduct();
        this.openUniStudentEnrolments$ = this.openUniversityEnrichedEnrolmentsService
            .getExistingEnrolmentsForCartItem(this.assessmentItemsData, this.openUniversityCart, this.product)
            .pipe(
                catchError((e: unknown) => {
                    // TODO: Remove this error handling in OTM-35745
                    if (e instanceof HttpErrorResponse && e.status === 403) {
                        // Unauthenticated existing students lose access to the enrolments made during the purchase
                        // after the payment has been completed successfully and the enrolments have been assigned
                        // to the existing PrivatePerson. Simply hide the enrolment info when this happens.
                        return of([]);
                    }
                    throw e;
                }),
            );
    }

    private getProductByCartItem(): OpenUniversityProduct {
        return this.openUniversityProducts.find(product => product.id === this.cartItem.openUniversityProductId);
    }

    private getCourseUnitByProduct(): CourseUnit {
        return this.courseUnits.find(courseUnit => courseUnit.id === this.product?.courseUnitId);
    }

    private getCompletionMethodByProduct(): CompletionMethod {
        return this.courseUnit?.completionMethods?.find(
            completionMethod =>
                this.product?.completionMethodId === completionMethod.localId && completionMethod.studyType === 'OPEN_UNIVERSITY_STUDIES',
        );
    }
}
